import { formatDate, formatTime, isEmptyValue } from '@root/helpers';
import { possibleValuesForOther } from '@root/helpers/constants';
import { countrySubdivisions } from '@root/helpers/schema/keywords/ensureSubDivisionBelongsToCountry';
import { isPPL } from '@root/helpers/utils';
import { get, intersection, isEmpty } from 'lodash-es';

export type BlockMentionValues = Record<string, string | number>;

const getParsedValue = (value: any, id: string, data: any, schemaField: any = {}, definitions?: any) => {
  let oneOfFromRef: any = null;
  if ('$ref' in schemaField && definitions) {
    const ref = schemaField['$ref'].split('#/definitions/')[1];
    oneOfFromRef = definitions[ref]?.oneOf;
  }

  if (schemaField.oneOf || oneOfFromRef) {
    if (possibleValuesForOther.includes(value)) {
      return get(data, `${id}_other`, '');
    }
    const constOrTitle = schemaField.shouldDisplayConstInsteadOfTitle ? 'const' : 'title';
    return (schemaField.oneOf || oneOfFromRef).find((item: any) => item.const === value)?.[constOrTitle] ?? value;
  }

  if (schemaField.items?.oneOf) {
    const selectedItems = value.map((itemId: string) => {
      if (possibleValuesForOther.includes(itemId)) {
        return get(data, `${id}_other`, '');
      }
      const selectedItem = schemaField.items.oneOf.find((option: any) => option.const === itemId);
      const constOrTitle = schemaField.shouldDisplayConstInsteadOfTitle ? 'const' : 'title';
      return selectedItem?.[constOrTitle] ?? itemId;
    });
    return selectedItems.join(', ');
  }

  if (schemaField['ui:component'] === 'InputCountrySubDivision' && isPPL) {
    return (
      Object.values(countrySubdivisions)
        .flatMap((country) => country.oneOf)
        .find((item) => item.const === value)?.title ?? value
    );
  }

  if (schemaField.displayTitleForYesAnswer) {
    return value === 'Yes' ? schemaField.title : ' ';
  }

  const intersectedOthers = intersection(value, possibleValuesForOther);
  if (Array.isArray(value) && intersectedOthers.length > 0) {
    const newArray = Object.assign([], value, { [value.indexOf(intersectedOthers[0])]: get(data, `${id}_other`, '') });
    return newArray.join(', ');
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'string' && possibleValuesForOther.includes(value)) {
    return get(data, `${id}_other`);
  }

  if (schemaField.format === 'date-time' || schemaField.format === 'date') {
    return formatDate(value, 'D MMMM YYYY') ?? '';
  }

  if (schemaField.format === 'time') {
    return formatTime(value);
  }

  if (schemaField['ui:component'] === 'InputPercentage') {
    return value.toString() + '%';
  }

  if (schemaField.type === 'number') {
    const shouldUseThousandsSeparator = schemaField.thousandSeparator ?? true;
    const isInteger = Number.isInteger(Number(value));

    return Intl.NumberFormat('en-GB', {
      useGrouping: shouldUseThousandsSeparator,
      minimumFractionDigits: isInteger ? 0 : 2,
    }).format(value);
  }

  return value.toString();
};

export const getMentionValues = (
  data: any,
  ids?: string[],
  schemaTreeList?: any,
  definitions?: any,
): BlockMentionValues | undefined => {
  if (!ids || isEmpty(ids)) return;
  if (!data || isEmpty(data)) return;

  return ids.reduce((acc, id) => {
    const value = get(data, id);
    const schemaField = schemaTreeList?.find((f: any) => f.id === id) || {};

    if (isEmptyValue(value)) {
      return { ...acc, [id]: undefined };
    }

    const parsedValue = getParsedValue(value, id, data, schemaField, definitions);

    return { ...acc, [id]: parsedValue };
  }, {});
};
